"use client";

import './index.css';
import { __rest } from '../../_virtual/_tslib.js';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { forwardRef, useRef, useId, useState, useEffect } from 'react';
import { validateSurface } from '../../globals.js';
import styles from './checkbox.module.scss.js';
import getNodeText from '../../utilities/get-node-text/index.js';
import ScreenReaderText from '../../utilities/screen-reader-text/index.js';
import '../../utilities/use-viewport/index.js';
import CheckmarkBold from '../../icons/component-icons/checkmark-bold/index.js';

/**
 * @component Checkbox
 * @package Checbox
 * @document ../_docs/code/Checkbox.editor.tsx
 * @document ../_docs/code/Checkbox.snippet.tsx
 * @document ../_docs/code/Checkbox.import.tsx
 */
const Checkbox = forwardRef((_a, ref) => {
    var { surface: surfaceProp = 'lightPrimary', label, errorText, error: errorProp, selected: selectedProp, onChange, disabled = false, required = true, width = '100%', inputId, id, name, value, onKeyDown, tabIndex, onBlur, onFocus, children, ariaLabel, 'aria-label': ariaLabelValue, ariaDescribedBy, 'data-testid': testId = 'input-checkbox', 'data-clickstream': clickStream, 'data-analyticstrack': analyticsTrack, 'data-track': track, 'data-track-ignore': ignoreTrack } = _a, rest = __rest(_a, ["surface", "label", "errorText", "error", "selected", "onChange", "disabled", "required", "width", "inputId", "id", "name", "value", "onKeyDown", "tabIndex", "onBlur", "onFocus", "children", "ariaLabel", 'aria-label', "ariaDescribedBy", 'data-testid', 'data-clickstream', 'data-analyticstrack', 'data-track', 'data-track-ignore']);
    const surface = validateSurface(surfaceProp);
    const surfaceStyles = styles[surface.toLowerCase()];
    const labelStyles = [styles.label, surfaceStyles];
    const checkboxInputStyles = [styles.checkboxInput];
    const checkboxIconHitAreaStyles = [styles.checkboxIconHitArea];
    const errorTextStyles = [styles.errorText, surfaceStyles];
    const wrapperStyles = [styles.wrapper];
    const checkboxIconInnerWrapperStyles = [
        styles.checkboxIconInnerWrapper,
        surfaceStyles,
    ];
    const checkboxIconSVGWrapperStyles = [
        styles.checkboxIconSVGWrapper,
        surfaceStyles,
    ];
    const checkboxId = useRef(null);
    const _useId = useId();
    if (checkboxId.current === null || checkboxId.current === undefined) {
        checkboxId.current = inputId || _useId;
    }
    const [selected, setSelected] = useState(selectedProp);
    const [error, setError] = useState(errorProp === true);
    const [isToggled, setIsToggled] = useState(false);
    const toggleTimer = useRef(null);
    useEffect(() => {
        if (toggleTimer.current) {
            clearTimeout(toggleTimer.current);
        }
        toggleTimer.current = setTimeout(() => {
            setIsToggled(false);
        });
    }, [selected]);
    const ariaLabelText = label
        ? `${label} ${getNodeText(children)}`
        : getNodeText(children);
    if (disabled) {
        labelStyles.push(styles.disabled);
        checkboxInputStyles.push(styles.disabled);
        checkboxIconHitAreaStyles.push(styles.disabled);
        checkboxIconInnerWrapperStyles.push(styles.disabled);
        wrapperStyles.push(styles.disabled);
        errorTextStyles.push(styles.disabled);
    }
    if (selected) {
        labelStyles.push(styles.selected);
        checkboxIconInnerWrapperStyles.push(styles.selected);
    }
    if (error && !selected) {
        checkboxIconInnerWrapperStyles.push(styles.error);
    }
    useEffect(() => {
        setSelected(selectedProp);
    }, [selectedProp]);
    useEffect(() => {
        if ((errorProp === true && !error) || (errorProp === false && error))
            setError(errorProp);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errorProp]);
    const _updateSelectedState = (e) => {
        setIsToggled(true);
        /* istanbul ignore else -- @preserve */
        if (onChange)
            onChange(e, {
                name,
                value,
                selected: !selected,
            });
        /* istanbul ignore else -- @preserve */
        if (selectedProp === undefined)
            setSelected(!selected);
        // check for error on checkbox change
        /* istanbul ignore if -- @preserve */
    };
    const _onKeyDown = (e) => {
        const keySelected = e.key;
        const escape = e.key !== 'Escape';
        const enterOrSpace = keySelected === 'Enter' || keySelected === ' ';
        /* istanbul ignore else -- @preserve */
        if (escape)
            e.stopPropagation(); // need to stop propogation here because youre handling the click event.
        if (enterOrSpace) {
            e.preventDefault();
            /* istanbul ignore else -- @preserve */
            if (onKeyDown)
                onKeyDown(e);
            // to return correct e.target.checked value when using enter/space keys
            e.target.checked = !selected;
            _updateSelectedState(e);
        }
    };
    const _onBlur = (e) => {
        /* istanbul ignore else -- @preserve */
        if (onBlur)
            onBlur(e);
        /* istanbul ignore else -- @preserve */
    };
    const renderInput = () => (jsxs(Fragment, { children: [jsxs("div", { className: styles.checkWrapper, children: [jsx(ScreenReaderText, { "aria-live": "assertive", children: isToggled && (ariaLabelValue || ariaLabel || ariaLabelText) }), jsx("input", Object.assign({ className: checkboxInputStyles.join(' '), checked: selected, required: required, "data-testid": testId, disabled: disabled, id: checkboxId.current || undefined, name: name, onChange: _updateSelectedState, onKeyDown: _onKeyDown, onBlur: _onBlur, onFocus: onFocus, tabIndex: tabIndex || 0, type: "checkbox", value: value, "aria-describedby": ariaDescribedBy }, (required && { 'aria-invalid': false }), { "aria-label": ariaLabelValue || ariaLabel || ariaLabelText, "data-clickstream": clickStream, "data-analyticstrack": analyticsTrack, "data-track": track, "data-track-ignore": ignoreTrack, ref: ref })), jsxs("span", { className: styles.checkboxIconWrapper, children: [jsx("div", { className: checkboxIconHitAreaStyles.join(' ') }), jsx("span", Object.assign({ className: checkboxIconInnerWrapperStyles.join(' ') }, rest, { children: selected && (jsx("div", { className: checkboxIconSVGWrapperStyles.join(' '), "aria-hidden": true, tabIndex: -1, children: jsx(CheckmarkBold, { size: 12 }) })) }))] })] }), (label || children) && (jsxs("span", { className: styles.textWrapper, "data-testid": `${testId}-label-text-wrapper`, children: [label && (jsx("span", { className: styles.labelWrapper, "aria-hidden": true, children: label })), children && (jsx("div", { className: styles.childWrapper, "aria-hidden": typeof children === 'string', children: children }))] }))] }));
    return (jsxs("div", { className: wrapperStyles.join(' '), style: {
            width,
        }, id: id, children: [jsx("label", { onClick: (e) => e.stopPropagation(), className: labelStyles.join(' '), htmlFor: checkboxId.current || undefined, children: renderInput() }), error && errorText && !selected && (jsx("div", { className: styles.errorWrapper, role: "alert", children: !disabled && (jsx("p", { className: errorTextStyles.join(' '), children: errorText })) }))] }));
});

export { Checkbox, Checkbox as default };
